import React from "react";
import { Token } from "../../types/tournament";
import FightMeter from "./FightMeter";
import TokenAvatar from "./TokenAvatar";
import TokenInfo from "./TokenInfo";
import { VictoryPhase } from "./types";

interface BattleHeaderProps {
  tokenA: Token;
  tokenB: Token;
  meterPosition: number;
  victoryPhase: VictoryPhase;
  scriptedWinner: "A" | "B";
  getDisplayLiquidity: (side: "A" | "B") => number;
  tokenARef: React.RefObject<HTMLDivElement>;
  tokenBRef: React.RefObject<HTMLDivElement>;
}

const BattleHeader: React.FC<BattleHeaderProps> = ({
  tokenA,
  tokenB,
  meterPosition,
  victoryPhase,
  scriptedWinner,
  getDisplayLiquidity,
  tokenARef,
  tokenBRef,
}) => {
  // Responsive sizing - smaller on mobile, larger on desktop
  const headerHeightClass = "h-32 sm:h-36 md:h-40";
  const avatarSizeClass = "w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32";
  
  const isTokenAEliminated = victoryPhase !== "idle" && scriptedWinner === "B";
  const isTokenBEliminated = victoryPhase !== "idle" && scriptedWinner === "A";
  const isTokenAWinner = victoryPhase === "complete" && scriptedWinner === "A";
  const isTokenBWinner = victoryPhase === "complete" && scriptedWinner === "B";
  const isTokenADead = isTokenAEliminated && victoryPhase === "complete";
  const isTokenBDead = isTokenBEliminated && victoryPhase === "complete";
  
  return (
    <header 
      className={`relative ${headerHeightClass} bg-gradient-to-b from-gray-900 to-black border-b border-cyan-900/50`}
    >
      <div className="relative h-full">
        {/* Token Avatars */}
        <TokenAvatar
          token={tokenA}
          side="left"
          sizeClass={avatarSizeClass}
          victoryPhase={victoryPhase}
          isEliminated={isTokenAEliminated}
          isWinner={isTokenAWinner}
          isDead={isTokenADead}
          innerRef={tokenARef}
        />
        
        <TokenAvatar
          token={tokenB}
          side="right"
          sizeClass={avatarSizeClass}
          victoryPhase={victoryPhase}
          isEliminated={isTokenBEliminated}
          isWinner={isTokenBWinner}
          isDead={isTokenBDead}
          innerRef={tokenBRef}
        />

        {/* Bottom section with token info and fight meter */}
        <div className="absolute bottom-0 left-0 right-0 pb-2">
          {/* Token Information Row */}
          <div className="flex justify-between items-end px-2 sm:px-4 mb-2 sm:mb-3 md:mb-4">
            <TokenInfo
              token={tokenA}
              side="left"
              isDead={isTokenADead}
            />
            
            <TokenInfo
              token={tokenB}
              side="right"
              isDead={isTokenBDead}
            />
          </div>

          {/* Fight Meter with integrated liquidity counters */}
          {/* On mobile: minimal margins, on larger screens: account for avatar space */}
          <div className="mx-4 sm:mx-16 md:mx-20 lg:mx-24 px-2 sm:px-4">
            <FightMeter
              position={meterPosition}
              victoryPhase={victoryPhase}
              winner={scriptedWinner}
              liquidityA={getDisplayLiquidity("A")}
              liquidityB={getDisplayLiquidity("B")}
              showLiquidity={true}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default BattleHeader;